"use client";

import { motion } from "motion/react";
import { useTranslations } from "next-intl";
import { useId, useState } from "react";
import {
    Accordion,
    AccordionDetails,
    AccordionSummary,
    Box,
    Button,
    Link,
    Stack,
    Typography,
} from "@/components/controls/base-controls";
import { AddIcon, RemoveIcon } from "@/components/controls/icons";
import { HEADER_HEIGHT_FULL, WEBPAGE_CORE_CONTAINER } from "@/core/constants/webpages";

const Services = () => {
    const t = useTranslations();

    const [expanded, setExpanded] = useState<string | false>(false);
    const panelId1 = useId();
    const panelId2 = useId();
    const panelId3 = useId();

    const handleChange =
        (panel: string) => (_event: React.SyntheticEvent, isExpanded: boolean) => {
            setExpanded(isExpanded ? panel : false);
        };

    return (
        <Box
            sx={{
                backgroundColor: "var(--mui-palette-lightest)",
                width: "100%",
                minHeight: {
                    xs: "100svh",
                    md: `calc(100svh - ${HEADER_HEIGHT_FULL})`,
                },
            }}
        >
            <Box
                sx={{
                    margin: "0 auto",
                    width: WEBPAGE_CORE_CONTAINER,
                    maxWidth: "100%",
                    padding: { xs: "3em 1em 2em", md: "4em 1em" },
                    display: "grid",
                    gridTemplateRows: { xs: "auto 1fr", md: "none" },
                    gridTemplateColumns: { xs: "none", md: "4fr 6fr" },
                    gap: { xs: "1em", md: "2em" }
                }}
            >
                <Stack
                    component={motion.div} 
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true, amount: 0.2 }}
                    sx={{ 
                        alignItems: "center",
                        justifyContent: "flex-start",
                        padding: "1em" }}
                >
                    <Typography
                        variant="h2"
                        sx={{
                            color: "var(--mui-palette-darkest)",
                            textTransform: "uppercase",
                        }}
                    >
                        {t("services.title")}
                    </Typography>
                </Stack>
                
                <Box sx={{ borderTop: "1px solid", borderColor: "divider", width: "100%", maxWidth: "44em" }}>
                    <Accordion
                        disableGutters
                        elevation={0}
                        expanded={expanded === "panel1"}
                        onChange={handleChange("panel1")}
                        sx={{ background: "transparent", borderBottom: "1px solid", borderColor: "divider", "&:before": { display: "none" } }}
                    >
                        <AccordionSummary
                            expandIcon={
                                expanded === "panel1" ? <RemoveIcon /> : <AddIcon />
                            }
                            aria-controls="panel1-content"
                            id={panelId1}
                        >
                            <Typography component="span" variant="body1">{t("services.accordions.panel1.summary")}</Typography>
                        </AccordionSummary>
                        <AccordionDetails sx={{paddingRight: "3em"}}>
                            <Typography variant="body1" component="div">
                                {t.rich("services.accordions.panel1.details", {
                                    p: (chunks) => <p style={{ margin: "0 0 1em" }}>{chunks}</p>
                                })}
                            </Typography>
                        </AccordionDetails>
                    </Accordion>
                    <Accordion
                        disableGutters
                        elevation={0}
                        expanded={expanded === "panel2"}
                        onChange={handleChange("panel2")}
                        sx={{ background: "transparent", borderBottom: "1px solid", borderColor: "divider", "&:before": { display: "none" } }}
                    >
                        <AccordionSummary
                            expandIcon={
                                expanded === "panel2" ? <RemoveIcon /> : <AddIcon />
                            }
                            aria-controls="panel2-content"
                            id={panelId2}
                        >
                            <Typography component="span">{t("services.accordions.panel2.summary")}</Typography>
                        </AccordionSummary>
                        <AccordionDetails sx={{paddingRight: "3em"}}>
                            <Typography variant="body1" component="div">
                                {t.rich("services.accordions.panel2.details", {
                                    p: (chunks) => <p style={{ margin: "0 0 1em" }}>{chunks}</p>
                                })}
                            </Typography>
                        </AccordionDetails>
                    </Accordion>
                    <Accordion
                        disableGutters
                        elevation={0}
                        expanded={expanded === "panel3"}
                        onChange={handleChange("panel3")}
                        sx={{ background: "transparent", borderBottom: "1px solid", borderColor: "divider", "&:before": { display: "none" } }}
                    >
                        <AccordionSummary
                            expandIcon={
                                expanded === "panel3" ? <RemoveIcon /> : <AddIcon />
                            }
                            aria-controls="panel3-content"
                            id={panelId3}
                        >
                            <Typography component="span">{t("services.accordions.panel3.summary")}</Typography>
                        </AccordionSummary>
                        <AccordionDetails sx={{paddingRight: "3em"}}>
                            <Typography variant="body1" component="div">
                                {t.rich("services.accordions.panel3.details", {
                                    p: (chunks) => <p style={{ margin: "0 0 1em" }}>{chunks}</p>
                                })}
                            </Typography>
                        </AccordionDetails>
                    </Accordion>

                </Box>

                <Button
                    variant="link" 
                    component={Link}
                    sx={{
                        margin: "2em 0 0 0",
                        padding: "1.2em 2.2em",
                        gridColumn: { md: "2" },
                        justifySelf: "center",
                    }}
                    href="/services"
                    >
                    {t("services.learn")}
                </Button>
                
            </Box>
        </Box>
    );
};

export default Services;
